/* eslint-disable no-plusplus */
import { makeGraphQLRequest } from '../../utils/requestUtils';
import { getOrgByID } from '../../graphql/organizationDetails';

export async function addToInventory({
  billsbyCustomerId,
  subscriptionLevel,
  email,
  previousPlan
}) {

  console.log('addToInventory called with:', {
    billsbyCustomerId, 
    subscriptionLevel,
    email,
    previousPlan
  });

  // Sanitize input fields to avoid validation errors
  const sanitizedEmail = email && email.trim ? email.trim() : email;
  const sanitizedLevel = subscriptionLevel && subscriptionLevel.trim ? subscriptionLevel.trim() : subscriptionLevel;

  // Check for required fields to avoid backend errors
  if (!sanitizedEmail) {
    console.error('Missing required field: email');
    return null;
  }
  
  if (!sanitizedLevel) {
    console.error('Missing required field: subscriptionLevel');
    return null;
  }
  
  // First, retrieve the current user data to get the complete billsbyCustomerId
  let fullBillsbyCustomerId = billsbyCustomerId;
  try {
    const userData = await getUserByEmail(sanitizedEmail);
    console.log('Current user data from API:', userData);
    
    if (userData && 
        userData.getUserByEmailOrganization) {
      
      // If the current billsbyCustomerId is a prefix of the stored one, use the full stored value
      if (userData.getUserByEmailOrganization.billsbyCustomerId) {
        const storedId = userData.getUserByEmailOrganization.billsbyCustomerId;
        if (storedId.startsWith(billsbyCustomerId)) {
          fullBillsbyCustomerId = storedId;
          console.log(`Using complete billsbyCustomerId from database: ${fullBillsbyCustomerId}`);
        }
      }
    }
  } catch (error) {
    console.warn('Could not retrieve full billsbyCustomerId, using provided value:', error);
  }

  const query = `mutation AddUserSubscription($user: EditUserSubscription) {
  addUserSubscription(user: $user) {
    organizationId
    billsbyCustomerId
    subscriptionLevel
    previousPlan
    email
    username
    id
  }
}`;

  // Prepare variables - only include valid fields the backend expects
  const variables = {
    user: {
        email: sanitizedEmail,
        subscriptionLevel: sanitizedLevel,
        billsbyCustomerId: fullBillsbyCustomerId,
        previousPlan
    }
  };
  
  console.log('Sending GraphQL mutation with variables:', JSON.stringify(variables));

  try {
    const response = await makeGraphQLRequest({
      query,
      variables,
      authCheck: false,
    });
    console.log('GraphQL addToInventory response:', response);
    return response;
  } catch (error) {
    console.error('Error in addToInventory GraphQL request:', error);
    // Return null instead of throwing to allow UI flow to continue
    return null;
  }
}


export async function getUserByEmail(email) {
  const query = `query GetUserByEmailOrganization($email: String) {
    getUserByEmailOrganization(email: $email) {
      id
      organizationName
      organizationId
      phoneNumber
      name
      subscriptionLevel
      billsbyCustomerId
      industryAssociations
      previousPlan
      policyAgreementDate
    }
  }`;

  // Variables
  const variables = {
    email
  };

  const response = await makeGraphQLRequest({
    query,
    variables,
    authCheck: false, // Assuming you want to perform an authorization check.
  });

  return response;
}

export async function agreeToPolicy(userId, email = null) {
  const query = `mutation AgreeToPolicy($userId: ID!, $email: String) {
    agreeToPolicy(userId: $userId, email: $email) {
      id
      policyAgreementDate
    }
  }`;

  const variables = {
    userId,
    ...(email ? { email } : {})
  };

  const response = await makeGraphQLRequest({
    query,
    variables,
  });

  return response;
}

export async function addToOrganizationMembers({
  billsbyCustomerId,
  subscriptionLevel,
  email,
  previousPlan,
  userRole
}) {
  console.log('addToOrganizationMembers called with:', {
    billsbyCustomerId,
    subscriptionLevel,
    email,
    previousPlan,
    userRole
  });

  try {
    const response = await addToInventory({billsbyCustomerId, subscriptionLevel, email, previousPlan});
    console.log('addToInventory response:', response);
    
    if (response && response.addUserSubscription && response.addUserSubscription.organizationId) {
      const organizationId = response.addUserSubscription.organizationId;
      console.log('Organization ID for members update:', organizationId);
      
      try {
        const companyInfo = await getOrgByID(organizationId);
        if (companyInfo && companyInfo.organization && companyInfo.organization.users) {
          console.log(`Found ${companyInfo.organization.users.length} organization members`);
          
          // Filter users based on role
          let usersToUpdate = companyInfo.organization.users.filter(
            user => user.email !== companyInfo.organization.email
          );
          
          // If the updating user is COMMON_ADMIN, only update COMMON_USER members
          // This ensures COMMON_USER inherits subscription from COMMON_ADMIN
          if (userRole === 'COMMON_ADMIN') {
            usersToUpdate = usersToUpdate.filter(user => user.role === 'COMMON_USER');
            console.log(`Filtering to COMMON_USER only: ${usersToUpdate.length} users to update`);
          }
          
          // Log the users we're going to update
          console.log(`Updating ${usersToUpdate.length} organization members`);
          
          // Use Promise.all to handle multiple updates in parallel
          await Promise.all(
            usersToUpdate.map(user => {
              console.log(`Updating member: ${user.email} (role: ${user.role})`);
              return addToInventory({
                billsbyCustomerId, 
                subscriptionLevel, 
                email: user.email,
                previousPlan
              });
            })
          );
        } else {
          console.error('Missing organization data:', companyInfo);
        }
      } catch (orgErr) {
        console.error('Error getting organization data:', orgErr);
      }
    } else {
      console.error('Invalid response from addToInventory:', response);
    }
    
    return response;
  } catch (error) {
    console.error('Error in addToOrganizationMembers:', error);
    throw error;
  }
}

export async function updateOrganizationMembers(organizationId) {
  const companyInfo = await getOrgByID(organizationId);
  const userInfo = await getUserByEmail(companyInfo.organization.email);
  console.log("updateOrganizationMembers companyInfo:", companyInfo);
  console.log("updateOrganizationMembers userInfo:", userInfo)
  const billsbyCustomerId = userInfo.getUserByEmailOrganization.billsbyCustomerId;
  const subscriptionLevel = userInfo.getUserByEmailOrganization.subscriptionLevel;
  companyInfo.organization.users.forEach((user) => {
    if (user.email !== companyInfo.organization.email) {
      addToInventory({billsbyCustomerId, subscriptionLevel, email: user.email})
    }
  })
}

export async function getOrganizationByName(name) {
  const query = `query GetOrganizationByName($name: String) {
    organizationByName(name: $name) {
      address
      city
      state
      country
      zipCode
    }
  }`;

  // Variables
  const variables = {
    name
  };

  const response = await makeGraphQLRequest({
    query,
    variables,
    authCheck: false, // Assuming you want to perform an authorization check.
  });

  return response;
}

export async function getAssociationValidations(id) {
  const query = `query organization($id: ID) {
    organization(id: $id) {
      id
      organizationId
      name
      email
      address1
      address2
      address3
      city
      state
      cntry
      industryAssociations
      associationValidations { name status expiration uniqueId pocUniqueId updated }
      inviteDate
      claimDate
    }
  }`;

  const variables = { id };

  const response = await makeGraphQLRequest({
    query,
    variables,
  });

  console.log("GetAssociationValidation:", response.organization);
  return response.organization;
}

export async function putIndustryAssociations(id, industryAssociations) {
  const query = `mutation updateOrganization($org: EditOrganizationInput) { updateOrganization (organization: $org) { id iconStatus industryAssociations } }`;
  const variables = {
    org: {
      industryAssociations,
      id,
    }
  };

  const response = await makeGraphQLRequest({
    query,
    variables,
    authCheck: false,
  });

  console.log("putIndustryAssociations:", response)
  return response
}

export async function putAssociationValidations(id, associationValidations) {
  const query = `mutation updateOrganization($org: EditOrganizationInput) { updateOrganization (organization: $org) { id iconStatus associationValidations { name status expiration uniqueId pocUniqueId updated } } }`;
  const variables = {
    org: {
      associationValidations,
      id,
    }
  };

  const response = await makeGraphQLRequest({
    query,
    variables,
    authCheck: false,
  });

  console.log("PutAssociationValidations:", response)
  return response
}


export async function putAssociations(id, industryAssociations, associationValidations) {
  const query = `mutation updateOrganization($org: EditOrganizationInput) { updateOrganization (organization: $org) { id iconStatus associationValidations { name status expiration uniqueId pocUniqueId updated} } }`;
  const variables = {
    org: {
      industryAssociations,
      associationValidations,
      id,
    }
  };

  const response = await makeGraphQLRequest({
    query,
    variables,
    authCheck: false,
  });

  console.log("PutAssociations:", response)
  return response
}


// export async function getCageCode(id) {
//   const query = `query requestOrganizationById(
//     $id: ID
//   ) {
//       organization(id: $id) {
//         cageCode
//       }
//     }`;

//   const variables = { id };
//   const response = await makeGraphQLRequest({
//     query,
//     variables,
//   });

//   return response;
// }

// export async function getOrgByID(id) {
//   // Format Query
//   const query = `query organization(
//     $id: ID
//   ) {
//     organization(id: $id) {
//       name
   
//       cageCode
     
//       }
//     }`;

//   // Variables
//   const variables = { id };

//   const response = await makeGraphQLRequest({
//     query,
//     variables,
//   });
//   return response.organization;
// }



// WEBPACK FOOTER //
// ./src/components/Billsby/graphql.js